import React, { useState } from "react";
import { CheckCircle2, Circle, Lock } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { useLocalization } from "../hooks/useLocalization";
import { COMPLETE_STATUSES } from "../lib/onboardingConstants";

export const RequirementChecklist = ({ requirements = [], canReview = false, onUpdateStatus, onSendReminder }) => {
  const { t, language } = useLocalization();
  const [savingId, setSavingId] = useState(null);

  const handleAction = async (req, status) => {
    setSavingId(req.id);
    try {
      await onUpdateStatus(req.id, status);
    } finally {
      setSavingId(null);
    }
  };

  const handleReminder = async (req) => {
    setSavingId(req.id);
    try {
      await onSendReminder(req.id);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="requirement-checklist">
      {requirements.map((req, idx) => {
        const complete = COMPLETE_STATUSES.includes(req.status);
        return (
          <div
            key={req.id}
            className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:shadow-sm transition-shadow animate-in"
            style={{ animationDelay: `${idx * 40}ms` }}
            data-testid={`requirement-item-${req.requirement_key}`}
          >
            <div className="mt-0.5">
              {complete ? <CheckCircle2 className="text-emerald-500" size={22} /> : <Circle className="text-gray-300" size={22} />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-gray-800 text-sm">{language === "ja" ? req.name_ja : req.name_en}</p>
                {req.blocking && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-500">
                    <Lock size={10} /> {t("onboarding.blocking")}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{language === "ja" ? req.description_ja : req.description_en}</p>
              <div className="mt-2"><StatusBadge status={req.status} /></div>
            </div>
            {canReview && !complete && req.status !== "NOT_APPLICABLE" && (
              <div className="flex flex-col gap-1.5 shrink-0">
                <button
                  data-testid={`approve-requirement-${req.requirement_key}`}
                  disabled={savingId === req.id}
                  onClick={() => handleAction(req, "APPROVED")}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50"
                >
                  {t("common.approve")}
                </button>
                <button
                  data-testid={`reject-requirement-${req.requirement_key}`}
                  disabled={savingId === req.id}
                  onClick={() => handleAction(req, "NEEDS_CORRECTION")}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-red-300 text-red-500 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-50"
                >
                  {t("common.reject")}
                </button>
                {onSendReminder && req.responsible_party === "EMPLOYEE" && (
                  <button
                    data-testid={`send-reminder-${req.requirement_key}`}
                    disabled={savingId === req.id}
                    onClick={() => handleReminder(req)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-blue-300 text-blue-600 hover:bg-blue-50 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {t("reminders.sendManualReminder")}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
